import React, { useState } from "react";
import ActionBar from "../../common/ActionBar";
import useLoader from "../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import AWSService from "../../../services/AWSService";
import { getErrorMessage, getFileName } from "../../../helpers/functions";
import { Formik } from "formik";
import styled from "styled-components";
import theme from "../../../assets/theme";
import { CustomFileUpload } from "../../common/FormInputs";
import COLORS from "../../../assets/Colors";
import PartySelect from "./component/PartySelect";
import { StyledFormElementsWrapper, Button } from "./Styles";
import FormField from "../../common/FormField/FormField";
import {
  useMediaQuery,
  useTheme,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  makeStyles,
} from "@material-ui/core";
import {
  PrimaryCTAButton,
  PrimaryOutlinedCTAButton,
} from "../../common/Buttons";
import { BulkNoticeGenerationSchema } from "../../../helpers/validationSchema";
import { navigate } from "@reach/router";
import NoticeService from "../../../services/NoticeService";
import ValidationErrors from "./component/ValidationError";
import Axios from "axios";
import useUser from "../../../hooks/useUser";

const input_alignments = [
  { label: "Left", value: 1 },
  { label: "Right", value: 3 },
  { label: "Center", value: 2 },
];

const useStyles = makeStyles((themes) => ({
  formLabel: {
    fontFamily: "openSans-SemiBold, sans-serif",
    color: "#293461",
  },
}));

/* eslint-disable jsx-a11y/anchor-is-valid */

const NoticeGeneration = () => {
  const [menuItems, setMenuItems] = useState([]);
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const themes = useTheme();
  const isBigScreen = useMediaQuery(themes.breakpoints.up(640));
  const [menuNotice, setMenuNotice] = useState([]);
  const [menuTemplateCode, setMenuTemplateCode] = useState([]);
  const [menuHeaderFooter, setMenuHeaderFooter] = useState([]);
  const [letterHeadArr, setLetterHeaderArr] = useState([]);
  const [noticeDesignTemplate, setNoticeDesignTemplate] = useState({});
  const [modal, setModal] = useState({ state: false });
  const classes = useStyles();
  const { userObject } = useUser();

  async function getNoticeTemplates(partyId) {
    try {
      setLoader({ state: true, message: "fetching notice details..." });
      const response = await NoticeService.getNoticeTemplates(partyId);
      if (response[0]) {
        setMenuNotice(
          response[0]?.noticeCategory?.map((item) => ({
            label: item,
            value: item,
          }))
        );
        setMenuTemplateCode(response[0]?.codeAndTemplates);
        const arrayOfObject = response[0]?.letterHeadArr.map((item) => ({
          label: item,
          value: item,
        }));
        setMenuHeaderFooter([{ label: "None", value: "" }, ...arrayOfObject]);
        setLetterHeaderArr(response[0]?.letterHeadOption);
        setNoticeDesignTemplate(response[0]?.designData?.designData);
      }
    } catch (err) {
      throw err;
    } finally {
      setLoader({ state: false });
    }
  }

  async function s3Upload(file, field, setFieldValue) {
    if (file) {
      try {
        const types = [
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          "application/vnd.ms-excel",
          "text/csv",
        ];
        setLoader({ state: true, message: `Uploading file ${file.name}` });
        const errorMessage = "Invalid File Type";
        if (!types.includes(file?.type)) throw errorMessage;
        const payload = {
          key: file.name,
        };
        const maxSize = 3 * 1024 * 1024;
        if (file.size > maxSize) {
          enqueueSnackbar("File size must be less than or equal to 3MB", {
            variant: "error",
          });
          return;
        }
        let fileNameWithoutExt = file.name.replace(
          /\s*\.(xlsx|pdf|docx)$/i,
          ""
        );
        const hasSpecialChar = /[^a-zA-Z0-9\s-_]/.test(fileNameWithoutExt);
        if (hasSpecialChar) {
          enqueueSnackbar("Filename contains invalid special characters", {
            variant: "error",
          });
        } else {
          const s3Response = await AWSService.getS3URL(payload);
          if (s3Response.url) {
            const res = await AWSService.uploadToS3(s3Response.url, file);
            setFieldValue(field, res);
          }
        }
      } catch (error) {
        const message = getErrorMessage(error);
        enqueueSnackbar(message, { variant: "error" });
      } finally {
        setLoader({ state: false });
      }
    }
  }

  async function onFormSubmit(values, { resetForm }) {
    try {
      setLoader({ state: true, message: "Please wait" });
      let noticeTemplatePayload = menuTemplateCode[
        values?.noticeCategory
      ].filter((template) => template?.templateCode === values?.templateCode)[0]
        ?.noticeTemplate;
      let noticeDesign =
        values?.noticeCopyDesign === "default"
          ? {
              lineSpacing: values?.lineSpacing,
              afterLineSpacing: values?.afterLineSpacing,
              topMargin: values?.topMargin,
              bottomMargin: values?.bottomMargin,
              leftMargin: values?.leftMargin,
              rightMargin: values?.rightMargin,
              headerDistance: values?.headerDistance,
              footerDistance: values?.footerDistance,
              fontSize: values?.fontSize,
            }
          : noticeDesignTemplate;
      const source =
        values?.letterHeadName &&
        letterHeadArr[values?.letterHeadName]?.length &&
        letterHeadArr[values?.letterHeadName][0];
      let letterHeadPayload;
      if (source) {
        if (source.letterHeadType === "image") {
          letterHeadPayload = {
            id: source.id,
            headerImage: source.headerImage || "",
            footerImage: source.footerImage || "",
            letterHeadType: "image",
          };
        } else if (source.letterHeadType === "text") {
          letterHeadPayload = {
            id: source.id,
            letterHeadType: "text",
            headerFooterText: {
              headerText: source.headerFooterText?.headerText || [],
              footerText: source.headerFooterText?.footerText || [],
            },
            headerAlignment: values?.headerAlignment || "left",
            footerAlignment: values?.footerAlignment || "left",
          };
        }
      } else {
        letterHeadPayload = { headerImage: "", footerImage: "" };
      }
      const payload = {
        partyId: values?.partyId,
        noticeCategory: values?.noticeCategory,
        templateCode: values?.templateCode,
        excelFile: values?.excelFile,
        lotNo: values?.lotNo,
        noticeTemplate: noticeTemplatePayload,
        letterHeadName: values?.letterHeadName,
        letterHeadImages: letterHeadPayload,
        noticeDesign,
        // ...(userObject?.isRequiredChecker && { checkerId: values?.checkerId }),
      };
      const res = await Axios.post(
        "https://mod.justact.co.in/check-keyerror",
        {
          noticeTemplate: noticeTemplatePayload,
          excelFile: values?.excelFile,
        }
      );
      if (res.data.message) {
        const response = await NoticeService.bulkNoticeGenerate(payload);
        if (response?.message) {
          enqueueSnackbar(response?.message, {
            variant: "success",
          });
          resetForm();
        }
      } else {
        setModal({ state: true, metaData: res.data });
      }
    } catch (err) {
      const message = getErrorMessage(err);
      enqueueSnackbar(message, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
    }
  }

  return (
    <Container>
      <ActionBar breadcrumbs={["Notice Generation"]} />
      <CreateContainer className="CreateCase">
        <Formik
          initialValues={{
            partyId: "",
            noticeCategory: "",
            templateCode: "",
            lotNo: "",
            checkerId: "",
            excelFile: "",
            letterHeadName: "",
            noticeCopyDesign: "",
            lineSpacing: 10,
            afterLineSpacing: 1,
            topMargin: 2.25,
            bottomMargin: 1.25,
            leftMargin: 1.5,
            rightMargin: 1.5,
            headerDistance: 0.25,
            footerDistance: 0.25,
            fontSize: 10,
            typeOfLetterHead: "image",
            headerAlignment: "left",
            footerAlignment: "left",
            userId: userObject?.isRequiredChecker,
          }}
          validationSchema={BulkNoticeGenerationSchema}
          enableReinitialize
          onSubmit={onFormSubmit}
          validateOnBlur
          validateOnChange
        >
          {({
            values,
            handleChange,
            errors,
            touched,
            handleBlur,
            handleSubmit,
            setFieldValue,
            setFieldError,
          }) => (
            <StyledForm>
              <PartySelect
                {...{
                  values,
                  handleChange,
                  handleSubmit,
                  errors,
                  touched,
                  handleBlur,
                  setFieldValue,
                  getNoticeTemplates,
                  setMenuItems,
                  menuItems,
                }}
              />
              <StyledFormElementsWrapper
                style={{ marginBottom: 15, marginTop: 15 }}
              >
                <div className="row">
                  <FormField
                    white
                    input={{
                      type: "select",
                      name: "noticeCategory",
                      label: "Notice Category",
                      required: true,
                    }}
                    menuItems={menuNotice}
                    {...{
                      values,
                      errors,
                      handleSubmit,
                      touched,
                      handleBlur,
                      setFieldValue,
                      handleChange,
                    }}
                  />
                </div>
              </StyledFormElementsWrapper>
              <StyledFormElementsWrapper
                style={{ marginBottom: 15, marginTop: 15 }}
              >
                <div className="row">
                  <FormField
                    white
                    input={{
                      type: "select",
                      name: "templateCode",
                      label: "Template Code",
                      required: true,
                    }}
                    menuItems={
                      menuTemplateCode[values?.noticeCategory]?.length
                        ? menuTemplateCode[values?.noticeCategory]?.map(
                            ({ templateCode }) => ({
                              label: templateCode,
                              value: templateCode,
                            })
                          )
                        : []
                    }
                    {...{
                      values,
                      handleChange,
                      errors,
                      handleSubmit,
                      touched,
                      handleBlur,
                      setFieldValue,
                    }}
                  />
                </div>
              </StyledFormElementsWrapper>
              <StyledFormElementsWrapper
                style={{ marginBottom: 15, marginTop: 15 }}
              >
                <div className="row">
                  <FormField
                    white
                    input={{
                      type: "select",
                      name: "noticeCopyDesign",
                      label: "Notice Copy Design",
                      required: false,
                    }}
                    menuItems={
                      menuItems?.filter(
                        (party) => party?.value === values?.partyId
                      )?.length
                        ? menuItems
                            ?.filter(
                              (party) => party?.value === values?.partyId
                            )
                            .map((item) => [
                              { label: "Default design", value: "default" },
                              { label: item.label, value: item.label },
                            ])[0]
                        : [{ label: "Default design", value: "default" }]
                    }
                    {...{
                      values,
                      handleChange,
                      errors,
                      handleSubmit,
                      touched,
                      handleBlur,
                      setFieldValue,
                    }}
                  />
                </div>
              </StyledFormElementsWrapper>
              <StyledFormElementsWrapper
                style={{ marginBottom: 15, marginTop: 15 }}
              >
                <FormLabel>
                  Upload excel template{" "}
                  <span className="required-star"> *</span>
                  <Subheading>
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href={
                        menuTemplateCode[values?.noticeCategory]?.length &&
                        menuTemplateCode[values?.noticeCategory].filter(
                          (template) =>
                            template?.templateCode === values?.templateCode
                        )[0]?.excelTemplate
                      }
                    >
                      Please download the excel template
                    </a>
                  </Subheading>
                </FormLabel>
                <CustomFileUpload
                  error={touched?.excelFile && errors?.excelFile}
                  setFile={(file) => s3Upload(file, `excelFile`, setFieldValue)}
                  style={{
                    marginTop: 3,
                    width: "100%",
                  }}
                  accept=".xlsx"
                  file={{ name: getFileName(values?.excelFile) }}
                />
              </StyledFormElementsWrapper>
              <StyledFormElementsWrapper
                style={{ marginBottom: 15, marginTop: 15 }}
              >
                <div className="disabledArrows">
                  <FormField
                    white
                    input={{
                      type: "number",
                      name: "lotNo",
                      label: "Lot Number",
                      required: true,
                    }}
                    {...{
                      values,
                      handleChange,
                      errors,
                      handleSubmit,
                      touched,
                      handleBlur,
                      setFieldValue,
                    }}
                  />
                </div>
              </StyledFormElementsWrapper>
              <StyledFormElementsWrapper
                style={{ marginBottom: 15, marginTop: 15 }}
              >
                <Grid
                  className={classes.radioRoot}
                  item
                  xs={12}
                  spacing={2}
                  style={{ marginBottom: "5px" }}
                >
                  <RadioGroup
                    style={{
                      flexDirection: "row",
                      marginLeft: "10px",
                    }}
                    name={"typeOfLetterHead"}
                    value={values.typeOfLetterHead}
                    onChange={(e) => {
                      setFieldValue("typeOfLetterHead", e.target.value);
                    }}
                  >
                    {[
                      {
                        label: "Image",
                        name: "image",
                        value: "image",
                      },
                      {
                        label: "Text",
                        name: "text",
                        value: "text",
                      },
                    ].map((radio, key) => (
                      <FormControlLabel
                        {...{ key }}
                        classes={{ label: classes.formLabel }}
                        value={radio.value}
                        control={
                          <Radio size="medium" style={{ color: "#00838c" }} />
                        }
                        label={radio.label}
                      />
                    ))}
                  </RadioGroup>
                </Grid>
                <div className="row">
                  <FormField
                    white
                    input={{
                      type: "select",
                      name: "letterHeadName",
                      label: "Letter Head Name",
                      required: false,
                    }}
                    menuItems={menuHeaderFooter}
                    {...{
                      values,
                      handleChange,
                      errors,
                      handleSubmit,
                      touched,
                      handleBlur,
                      setFieldValue,
                    }}
                  />
                  {values?.typeOfLetterHead === "text"
                    ? [
                        {
                          type: "select",
                          name: "headerAlignment",
                          label: "Header Alignment",
                          required: false,
                          items: input_alignments,
                        },
                        {
                          type: "select",
                          name: "footerAlignment",
                          label: "Footer Alignment",
                          required: false,
                          items: input_alignments,
                        },
                      ].map((input, index) => {
                        return (
                          <FormField
                            key={index}
                            menuItems={input.items}
                            {...{
                              touched,
                              errors,
                              input,
                              values,
                              handleChange,
                              handleBlur,
                              setFieldValue,
                            }}
                          />
                        );
                      })
                    : null}
                </div>
              </StyledFormElementsWrapper>
              {/*
              {userObject?.isRequiredChecker ? (
                <StyledFormElementsWrapper
                  style={{ marginBottom: 15, marginTop: 15 }}
                >
                  <div className="disabledArrows">
                    <FormField
                      white
                      input={{
                        type: "select",
                        name: "checkerId",
                        label: "Select a checker",
                        required: true,
                      }}
                      menuItems={agents
                        .filter(({ partyId }) => partyId === values.partyId)
                        .map(({ email, userId, invitedById }) => ({
                          label: email,
                          value: userId,
                          userId: userId,
                          invitedById: invitedById,
                        }))}
                      {...{
                        values,
                        handleChange,
                        errors,
                        handleSubmit,
                        touched,
                        handleBlur,
                        setFieldValue,
                      }}
                    />
                  </div>
                </StyledFormElementsWrapper>
              ) : null}
               */}
              <StyledFormElementsWrapper
                style={{
                  display: "flex",
                  justifyContent: isBigScreen ? "space-between" : "flex-start",
                  marginTop: "25px",
                }}
              >
                <Button
                  style={{
                    marginLeft: "unset",
                  }}
                >
                  <PrimaryOutlinedCTAButton
                    onClick={() => navigate(`/dashboard`)}
                  >
                    Back
                  </PrimaryOutlinedCTAButton>
                </Button>
                <Button
                  style={{
                    marginLeft: "22px",
                  }}
                >
                  <PrimaryCTAButton onClick={handleSubmit}>
                    Generate Notice
                  </PrimaryCTAButton>
                </Button>
              </StyledFormElementsWrapper>
            </StyledForm>
          )}
        </Formik>
      </CreateContainer>
      {modal?.state && <ValidationErrors {...{ modal, setModal }} />}
    </Container>
  );
};

export default NoticeGeneration;

const Container = styled.div`
  border-radius: 10px;
  background-color: ${COLORS.PRIMARY_WHITE};
  margin: 40px 15px;
  padding-top: 1px;
  @media ${theme?.breakpoints?.sm_up} {
    margin: 0;
    padding-top: 0;
    border-radius: 0;
    background-color: transparent;
  }
`;

const CreateContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 12px;
  padding-bottom: 12px;
  padding-left: 13px;
  padding-right: 11px;
  @media ${theme?.breakpoints?.sm_up} {
    padding-left: 46px;
    padding-right: 0;
    margin-top: 0;
    padding-bottom: 0;
  }
`;

const StyledForm = styled.form`
  @media ${theme?.breakpoints?.sm_up} {
    width: 505px;
  }
  & .heading {
    display: flex;
    align-items: center;
    flex: 1;
    justify-content: space-between;
    & .delete {
      height: 20px;
      width: 20px;
      margin-bottom:15px;
      cursor: pointer;
      @media ${theme?.breakpoints?.sm_up} {
        margin: 34px 0 21px 0;
      }
    }
`;

const Subheading = styled.div`
  margin-bottom: 5px;
  & a {
    margin-left: 10px;
    font-family: ${theme.fonts.primaryFontBold};
    color: ${COLORS.BTN_GREEN};
    text-decoration: underline;
    cursor: pointer;
  }
`;

const FormLabel = styled.div`
  display: flex;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 12px;
  line-height: 1.7;
  text-transform: capitalize;
  color: ${COLORS.INPUT_LABEL};
  margin-right: 8px;
`;
